import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { uploadAudioBlob } from './audio';
import { deleteBlob, getBlob, putBlob } from './blobStore';

// Web port of app/src/lib/outbox.ts. Same two-phase state machine
// (enqueueRecording / finalizeOutbox) so upload runs in parallel with
// transcription. Differences:
//   - Queue metadata lives in localStorage (was AsyncStorage)
//   - Audio blobs live in IndexedDB (was expo-file-system)
//   - No app-state listener; we use visibilitychange + a drain-on-load

const STORAGE_KEY = 'hive.outbox.v1';
const BACKOFF_MS = [1_000, 4_000, 16_000, 64_000, 256_000];
const MAX_ATTEMPTS = 5;

export type OutboxState = 'pending' | 'sending' | 'failed';

export type OutboxTimings = {
  recorded_at: number;
  record_ms: number;
  transcribe_ms: number | null;
  upload_ms: number | null;
  insert_ms: number | null;
  total_ms: number | null;
};

export type OutboxItem = {
  id: string;
  sender_id: string;
  group_id: string;
  recipient_id: string | null;
  mime_type: string;
  uploaded_path: string | null;
  transcript: string | null;
  duration_ms: number;
  urgency: 'urgent' | null;
  finalized: boolean;
  state: OutboxState;
  attempts: number;
  next_attempt_at: number;
  last_error: string | null;
  timings: OutboxTimings;
};

type Listener = () => void;
const listeners = new Set<Listener>();
function notify() {
  for (const l of listeners) {
    try {
      l();
    } catch {}
  }
}
export function subscribeOutbox(fn: Listener) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export type DeliveryEvent = { id: string; total_ms: number | null };
type DeliveryListener = (e: DeliveryEvent) => void;
const deliveryListeners = new Set<DeliveryListener>();
export function onDelivery(fn: DeliveryListener) {
  deliveryListeners.add(fn);
  return () => {
    deliveryListeners.delete(fn);
  };
}
function emitDelivery(e: DeliveryEvent) {
  for (const l of deliveryListeners) {
    try {
      l(e);
    } catch {}
  }
}

function loadAll(): OutboxItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as OutboxItem[];
  } catch {
    return [];
  }
}

function saveAll(items: OutboxItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    console.warn('[outbox] localStorage save failed', e);
  }
  notify();
}

// localStorage is synchronous, no write chain needed — but we still serialize
// mutations behind a promise chain so concurrent callers see a consistent view.
let writeChain: Promise<void> = Promise.resolve();
function mutate(fn: (items: OutboxItem[]) => OutboxItem[] | Promise<OutboxItem[]>): Promise<void> {
  const next = writeChain.then(async () => {
    const current = loadAll();
    const updated = await fn(current);
    saveAll(updated);
  });
  writeChain = next.catch(() => {});
  return next;
}

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Two-phase: persist blob + enqueue with no routing, so upload can start
// while transcription runs. Caller finalizes with routing once done.
export async function enqueueRecording(fields: {
  sender_id: string;
  group_id: string;
  blob: Blob;
  mime_type: string;
  duration_ms: number;
  timings: OutboxTimings;
}): Promise<string> {
  const id = newId();
  await putBlob(id, fields.blob);
  const item: OutboxItem = {
    id,
    sender_id: fields.sender_id,
    group_id: fields.group_id,
    recipient_id: null,
    mime_type: fields.mime_type,
    uploaded_path: null,
    transcript: null,
    duration_ms: fields.duration_ms,
    urgency: null,
    finalized: false,
    state: 'pending',
    attempts: 0,
    next_attempt_at: Date.now(),
    last_error: null,
    timings: fields.timings,
  };
  await mutate((items) => [...items, item]);
  scheduleDrain(0);
  return id;
}

// Fully-specified enqueue (voice commands that know their routing).
export async function enqueue(fields: {
  sender_id: string;
  group_id: string;
  recipient_id: string | null;
  blob: Blob;
  mime_type: string;
  transcript: string | null;
  duration_ms: number;
  urgency: 'urgent' | null;
  timings: OutboxTimings;
}): Promise<string> {
  const id = newId();
  await putBlob(id, fields.blob);
  const item: OutboxItem = {
    id,
    sender_id: fields.sender_id,
    group_id: fields.group_id,
    recipient_id: fields.recipient_id,
    mime_type: fields.mime_type,
    uploaded_path: null,
    transcript: fields.transcript,
    duration_ms: fields.duration_ms,
    urgency: fields.urgency,
    finalized: true,
    state: 'pending',
    attempts: 0,
    next_attempt_at: Date.now(),
    last_error: null,
    timings: fields.timings,
  };
  await mutate((items) => [...items, item]);
  scheduleDrain(0);
  return id;
}

export async function finalizeOutbox(
  id: string,
  meta: {
    recipient_id: string | null;
    transcript: string | null;
    urgency: 'urgent' | null;
    transcribe_ms: number | null;
  }
): Promise<void> {
  await mutate((items) =>
    items.map((i) => {
      if (i.id !== id) return i;
      return {
        ...i,
        recipient_id: meta.recipient_id,
        transcript: meta.transcript,
        urgency: meta.urgency,
        finalized: true,
        state: i.state === 'failed' ? 'failed' : 'pending',
        next_attempt_at: Date.now(),
        timings: { ...i.timings, transcribe_ms: meta.transcribe_ms },
      };
    })
  );
  scheduleDrain(0);
}

export async function abandonOutbox(id: string): Promise<void> {
  let doomed: OutboxItem | null = null;
  await mutate((items) => {
    const it = items.find((x) => x.id === id);
    if (it) doomed = it;
    return items.filter((x) => x.id !== id);
  });
  if (doomed) await cleanupItemFiles(doomed);
}

async function cleanupItemFiles(item: OutboxItem): Promise<void> {
  try {
    await deleteBlob(item.id);
  } catch {}
  if (item.uploaded_path) {
    try {
      const { error } = await supabase.storage.from('audio').remove([item.uploaded_path]);
      if (error) console.warn('[outbox] storage cleanup failed', error.message);
    } catch (e: any) {
      console.warn('[outbox] storage cleanup threw', e?.message ?? String(e));
    }
  }
}

export async function retryAll(): Promise<void> {
  const now = Date.now();
  await mutate((items) =>
    items.map((i) =>
      i.state === 'failed'
        ? { ...i, state: 'pending', attempts: 0, next_attempt_at: now, last_error: null }
        : i
    )
  );
  scheduleDrain(0);
}

export async function discardItem(id: string): Promise<void> {
  let doomed: OutboxItem | null = null;
  await mutate((items) => {
    const it = items.find((x) => x.id === id);
    if (it) doomed = it;
    return items.filter((x) => x.id !== id);
  });
  if (doomed) await cleanupItemFiles(doomed);
}

function backoffFor(attempts: number): number {
  return BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length - 1)];
}

function actionable(i: OutboxItem, now: number): boolean {
  if (i.state !== 'pending') return false;
  if (i.next_attempt_at > now) return false;
  if (!i.uploaded_path) return true;
  return i.finalized;
}

let draining = false;
let drainTimer: ReturnType<typeof setTimeout> | null = null;

export function scheduleDrain(delayMs: number): void {
  if (drainTimer) clearTimeout(drainTimer);
  drainTimer = setTimeout(() => {
    drainTimer = null;
    drain().catch(() => {});
  }, Math.max(0, delayMs));
}

async function drain(): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    while (true) {
      const items = loadAll();
      const now = Date.now();
      const next = items.find((i) => actionable(i, now));
      if (!next) {
        const soonest = items
          .filter((i) => i.state === 'pending' && (!i.uploaded_path || i.finalized))
          .map((i) => i.next_attempt_at - now)
          .sort((a, b) => a - b)[0];
        if (typeof soonest === 'number' && isFinite(soonest)) {
          scheduleDrain(Math.max(soonest, 500));
        }
        return;
      }
      await processOne(next);
    }
  } finally {
    draining = false;
  }
}

async function processOne(item: OutboxItem): Promise<void> {
  await mutate((items) => items.map((i) => (i.id === item.id ? { ...i, state: 'sending' } : i)));

  if (!item.uploaded_path) {
    try {
      const blob = await getBlob(item.id);
      if (!blob) throw new Error('Audio blob missing from IndexedDB');
      const uploadStart = Date.now();
      const path = await uploadAudioBlob(blob, item.sender_id, item.mime_type);
      const upload_ms = Date.now() - uploadStart;
      await mutate((items) =>
        items.map((i) =>
          i.id === item.id
            ? {
                ...i,
                uploaded_path: path,
                state: 'pending',
                next_attempt_at: Date.now(),
                timings: { ...i.timings, upload_ms },
              }
            : i
        )
      );
      return;
    } catch (e: any) {
      await recordFailure(item, e);
      return;
    }
  }

  try {
    const { error } = await supabase.from('messages').insert({
      sender_id: item.sender_id,
      group_id: item.group_id,
      recipient_id: item.recipient_id,
      audio_path: item.uploaded_path,
      transcript: item.transcript,
      duration_ms: item.duration_ms,
      urgency: item.urgency,
    });
    if (error) throw error;
    const total_ms = Date.now() - item.timings.recorded_at;

    console.log('[outbox] sent', { id: item.id, attempts: item.attempts + 1, total_ms });

    await mutate((items) => items.filter((i) => i.id !== item.id));
    try {
      await deleteBlob(item.id);
    } catch {}
    emitDelivery({ id: item.id, total_ms });
  } catch (e: any) {
    await recordFailure(item, e);
  }
}

async function recordFailure(item: OutboxItem, e: any): Promise<void> {
  const attempts = item.attempts + 1;
  const willRetry = attempts < MAX_ATTEMPTS;
  const msg = e?.message ?? String(e);
  console.warn('[outbox] send failed', { id: item.id, attempts, msg, willRetry });
  await mutate((items) =>
    items.map((i) =>
      i.id === item.id
        ? {
            ...i,
            state: willRetry ? 'pending' : 'failed',
            attempts,
            next_attempt_at: Date.now() + backoffFor(attempts - 1),
            last_error: msg,
          }
        : i
    )
  );
}

let started = false;
export function startOutbox(): void {
  if (started) return;
  started = true;
  // Reset anything stuck in 'sending' from a prior tab session.
  mutate((items) =>
    items.map((i) =>
      i.state === 'sending' ? { ...i, state: 'pending', next_attempt_at: Date.now() } : i
    )
  ).then(() => scheduleDrain(0));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleDrain(0);
  });
}

export function useOutbox(): {
  items: OutboxItem[];
  pending: number;
  sending: number;
  failed: number;
  awaitingMeta: number;
} {
  const [items, setItems] = useState<OutboxItem[]>(() => loadAll());
  useEffect(() => {
    return subscribeOutbox(() => setItems(loadAll()));
  }, []);
  let pending = 0;
  let sending = 0;
  let failed = 0;
  let awaitingMeta = 0;
  for (const i of items) {
    if (i.state === 'sending') sending++;
    else if (i.state === 'failed') failed++;
    else if (i.state === 'pending') {
      if (!i.finalized) awaitingMeta++;
      else pending++;
    }
  }
  return { items, pending, sending, failed, awaitingMeta };
}
